/**
 * Scoring shared by the single-rule forward paper tests (the ETH hourly rule
 * and Riptide): one bet at an ask, held to settlement, judged against a pass
 * bar fixed before the test began. Pure logic: no network, no storage.
 */
import {
  PRICE_RULE_STAKE_DOLLARS,
  netPerContract,
  stakePnl,
  type Side,
  type Verdict,
} from './btc15PriceRules';

/**
 * Below this many settled bets a win rate or per-contract figure is noise
 * (one lost bet reads as "0.0% win rate"), so scorecards show the count and
 * nothing else. Display only: it never affects a verdict.
 */
export const MIN_READABLE_BETS = 20;

export interface ForwardBet {
  side: Side;
  ask: number;
  /** The same side's ask one minute later: a realistic-fill stress. */
  nextAsk?: number | null;
  result?: Side;
}

export interface ForwardPassBar {
  minBets: number;
  /** Null when a win-rate floor makes no sense for the rule's price. */
  minWinRate: number | null;
  /** Net per contract after fees must exceed this... */
  minNetPerContract: number;
  /** ...and the lower end of its ~95% range must exceed this (dollars). */
  minNetLowerBound: number;
}

export interface ForwardRuleSummary {
  rule: string;
  label: string;
  entered: number;
  settled: number;
  wins: number;
  winRate: number | null;
  avgAsk: number | null;
  /** Average net per contract after fees (dollars). */
  netPerContract: number | null;
  /** ~95% range on netPerContract (dollars). */
  netLow: number | null;
  netHigh: number | null;
  /** Total P&L at a flat PRICE_RULE_STAKE_DOLLARS per bet. */
  stakePnl: number;
  /** Same, buying at the worse of this and the next minute's ask (bets with one recorded). */
  stakePnlNextFill: number;
  withNextFill: number;
  verdict: Verdict;
}

/**
 * Scores a rule on its settled bets. The verdict is "collecting" until the
 * pre-registered bet count is reached, then passing/failing against the full
 * bar. Never changes a rule; it only reports.
 */
export function summarizeForwardRule(
  bets: readonly ForwardBet[],
  spec: { id: string; label: string },
  bar: ForwardPassBar,
  stake = PRICE_RULE_STAKE_DOLLARS,
): ForwardRuleSummary {
  const settled = bets.filter((d) => d.result !== undefined);
  const n = settled.length;
  const nets = settled.map((d) => netPerContract(d.ask, d.result === d.side));
  const wins = settled.filter((d) => d.result === d.side).length;
  const net = n ? nets.reduce((a, b) => a + b, 0) / n : null;
  const se =
    n > 1 && net !== null
      ? Math.sqrt(nets.reduce((a, x) => a + (x - net) ** 2, 0) / (n - 1) / n)
      : null;
  const netLow = net !== null && se !== null ? net - 1.96 * se : null;
  const winRate = n ? wins / n : null;
  let dollars = 0;
  let dollarsNext = 0;
  let withNext = 0;
  for (const d of settled) {
    const won = d.result === d.side;
    dollars += stakePnl(d.ask, won, stake) ?? 0;
    if (d.nextAsk !== undefined && d.nextAsk !== null) {
      withNext++;
      dollarsNext += stakePnl(Math.max(d.ask, d.nextAsk), won, stake) ?? 0;
    }
  }
  const verdict: Verdict =
    n < bar.minBets
      ? 'collecting'
      : (bar.minWinRate === null || (winRate !== null && winRate >= bar.minWinRate)) &&
          net !== null &&
          net > bar.minNetPerContract &&
          netLow !== null &&
          netLow > bar.minNetLowerBound
        ? 'passing'
        : 'failing';
  return {
    rule: spec.id,
    label: spec.label,
    entered: bets.length,
    settled: n,
    wins,
    winRate,
    avgAsk: n ? settled.reduce((a, d) => a + d.ask, 0) / n : null,
    netPerContract: net,
    netLow,
    netHigh: net !== null && se !== null ? net + 1.96 * se : null,
    stakePnl: Number(dollars.toFixed(2)),
    stakePnlNextFill: Number(dollarsNext.toFixed(2)),
    withNextFill: withNext,
    verdict,
  };
}
